import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const easeExpo = [0.16, 1, 0.3, 1];

const NAV_LINKS = [
  { href: "/#manifesto", label: "Process" },
  { href: "/#services", label: "Services" },
  { href: "/#industries", label: "Industries" },
  { href: "/#founders", label: "Team" },
  { href: "/blog", label: "Blog" },
  { href: "/#contact", label: "Contact" },
];

const CMYKBar = () => (
  <div className="cmyk-bar cmyk-bar-thin">
    <div className="c" /><div className="m" /><div className="y" /><div className="k" />
  </div>
);

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: easeExpo, delay: 0.4 }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b-2 border-black" : "bg-white/70 backdrop-blur-sm"
      }`}
      data-testid="site-header"
    >
      <nav className="max-w-[1600px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src="/logos/gm-logo-light.png" alt="GM Print" width={44} height={44} />
          <div className="hidden sm:block">
            <div className="font-display text-lg leading-none">GM PRINT</div>
            <div className="font-mono text-[9px] tracking-widest mt-0.5">GAIN MOMENTUM</div>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            l.href.startsWith("/#") ? (
              <a key={l.href} href={l.href} className="nav-link" data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.label}
              </a>
            ) : (
              <Link key={l.href} to={l.href} className="nav-link" data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.label}
              </Link>
            )
          ))}
          <a href="/#contact" className="btn-primary" data-testid="nav-get-quote-btn">
            Get Your Free Quote
          </a>
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>
      <CMYKBar />

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-b-2 border-black overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                l.href.startsWith("/#") ? (
                  <a key={l.href} href={l.href} className="nav-link block py-2" onClick={() => setIsOpen(false)}>
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.href} to={l.href} className="nav-link block py-2" onClick={() => setIsOpen(false)}>
                    {l.label}
                  </Link>
                )
              ))}
              <a href="/#contact" className="btn-primary justify-center mt-2" onClick={() => setIsOpen(false)}>
                Get Your Free Quote
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
